import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Snowflake, MapPin, Phone, Mail, Facebook, Instagram, Youtube } from "lucide-react";
import { useResponsive } from "../hooks/useResponsive";
import { colors } from "../styles/colors";
import { useCookieConsent } from "../context/CookieConsentContext";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const { openSettings } = useCookieConsent();
  const currentYear = new Date().getFullYear();
  const locale = (i18n?.language || "en").split("-")[0];
  const langPrefix = `/${locale}`;
  const normalizedAddressLine1 =
    locale === "sv"
      ? "Råstrand, Svenska Lappland"
      : locale === "de"
      ? "Råstrand, Schwedisch-Lappland"
      : "Råstrand, Swedish Lapland";
  const normalizedPhone = "+46 73 181 45 68";

  const quickLinks = [
    { name: t("footer.quickLinks.home"), path: `${langPrefix}/` },
    { name: t("footer.quickLinks.about"), path: `${langPrefix}/about` },
    { name: t("footer.quickLinks.packages"), path: `${langPrefix}/packages` },
    { name: t("footer.quickLinks.gallery"), path: `${langPrefix}/gallery` },
    { name: t("footer.quickLinks.contact"), path: `${langPrefix}/contact` },
  ];

  const experiences = [
    t("footer.experiences.snowmobile"),
    t("footer.experiences.northernLights"),
    t("footer.experiences.dogSledding"),
    t("footer.experiences.saunaHotTub"),
    t("footer.experiences.iceFishing"),
    t("footer.experiences.localCuisine"),
  ];

  return (
    <footer className="text-white" style={{ background: colors.gradients.heroSolid }}>
      <div
        className={`mx-auto ${
          isMobile ? "max-w-sm px-4 py-12" : "max-w-7xl px-4 sm:px-6 lg:px-8 py-16"
        }`}
      >
        <div
          className={`grid gap-8 ${
            isMobile ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
          }`}
        >
          <div className={isMobile ? "" : "lg:col-span-1"}>
            <div className={`flex items-center space-x-2 mb-6 ${isMobile ? "justify-center" : ""}`}>
              <Snowflake className={isMobile ? "h-6 w-6 text-cyan-400" : "h-8 w-8 text-cyan-400"} />
              <span className={isMobile ? "text-xl font-bold" : "text-2xl font-bold"}>
                {t("footer.companyName")}
              </span>
            </div>
            <p className={`${isMobile ? "text-sm text-center" : ""} text-gray-300 mb-6 leading-relaxed`}>
              {t("footer.companyDescription")}
            </p>
            <div className={`flex space-x-4 ${isMobile ? "justify-center" : ""}`}>
              <a
                href="https://www.facebook.com/coldexperience.se/"
                target="_blank"
                rel="noreferrer"
                className="bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors duration-300"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/coldexperience.se/"
                target="_blank"
                rel="noreferrer"
                className="bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@coldexperience"
                target="_blank"
                rel="noreferrer"
                className="bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors duration-300"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div className={isMobile ? "text-center" : ""}>
            <h3 className={`${isMobile ? "text-lg" : "text-xl"} font-semibold mb-6 text-cyan-400`}>
              {t("footer.quickLinks.title")}
            </h3>
            <ul className={`space-y-3 ${isMobile ? "text-center" : ""}`}>
              {quickLinks.map(({ name, path }) => (
                <li key={path}>
                  <Link
                    to={path}
                    className={`transition-colors duration-200 flex items-center group hover:opacity-80 ${
                      isMobile ? "justify-center text-sm" : ""
                    }`}
                    style={{ color: colors.text.secondary }}
                  >
                    <span className="w-1 h-1 bg-cyan-400 rounded-full mr-3 group-hover:w-2 transition-all duration-200" />
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className={isMobile ? "text-center" : ""}>
            <h3 className="text-xl font-semibold mb-6" style={{ color: colors.accent.hover }}>
              {t("footer.experiences.title")}
            </h3>
            <ul className="space-y-3">
              {experiences.map((experience, index) => (
                <li key={index} className="text-gray-300 flex items-center">
                  <span className="w-1 h-1 bg-blue-400 rounded-full mr-3" />
                  {experience}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-6 text-green-400">
              {t("footer.contact.title")}
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">{normalizedAddressLine1}</p>
                  <p className="text-gray-400 text-sm">{t("footer.contact.addressLine2")}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 flex-shrink-0" style={{ color: colors.accent.hover }} />
                <p className="text-gray-300">{normalizedPhone}</p>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                <p className="text-gray-300">{t("footer.contact.email")}</p>
              </div>
            </div>
            <div
              className="mt-6 p-4 rounded-lg"
              style={{ backgroundColor: colors.glass.medium, border: `1px solid ${colors.border}` }}
            >
              <h4 className="font-semibold text-white mb-2">{t("footer.season.title")}</h4>
              <p className="text-gray-300 text-sm">{t("footer.season.dates")}</p>
              <p className="text-gray-400 text-xs">{t("footer.season.cta")}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} {t("footer.companyName")}
            </div>
            <div className="flex space-x-6 text-sm text-gray-400">
              <Link to={`${langPrefix}/privacy`} className="hover:text-white transition-colors">
                {t("footer.privacyPolicy")}
              </Link>
              <Link to={`${langPrefix}/terms`} className="hover:text-white transition-colors">
                {t("footer.termsOfService")}
              </Link>
              <Link to={`${langPrefix}/cookies`} className="hover:text-white transition-colors">
                {t("footer.cookiePolicy")}
              </Link>
              <button
                type="button"
                onClick={openSettings}
                className="bg-transparent p-0 text-left hover:text-white transition-colors"
                style={{ border: "none" }}
              >
                {t("footer.cookieSettings")}
              </button>
            </div>
          </div>
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm italic">{t("footer.quote")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
